import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/auth-context';
import { getTemplateWords } from '../config';
import { CONSOLE_MESSAGES, INPUT_LIMITS } from '../constants';
import { SPINNER_CLASSES, BUTTON_CLASSES, cn } from '../styles';

function WatchedModal({ isOpen, onClose, item, mediaType }) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addToWatched, removeFromWatched, getWatchedItem } = useAuth();

  const currentTemplateWords = getTemplateWords(rating);

  // Get current watched item
  const watchedItem = getWatchedItem(item?.id, mediaType);
  const isAlreadyWatched = !!watchedItem;

  // Initialize with existing data if already watched
  useEffect(() => {
    if (isOpen && item) {
      if (watchedItem) {
        setRating(watchedItem.rating || 0);
        setComment(watchedItem.comment || '');
      } else {
        setRating(0);
        setComment('');
      }
    }
  }, [isOpen, item, mediaType, watchedItem]);

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle star rating click
  const handleRatingClick = (selectedRating) => {
    setRating(selectedRating);
  };

  // Handle template word click
  const handleTemplateWordClick = (word) => {
    const newComment = comment ? `${comment} ${word}` : word;
    if (newComment.length <= INPUT_LIMITS.COMMENT_MAX_LENGTH) {
      setComment(newComment);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await addToWatched(item.id, mediaType, rating || null, comment.trim());
      onClose();
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_ADDING_TO_WATCHED, error);
      // You could add a toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove from watched
  const handleRemove = async () => {
    setIsLoading(true);

    try {
      await removeFromWatched(item.id, mediaType);
      onClose();
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_REMOVING_FROM_WATCHED, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setRating(0);
    setComment('');
    onClose();
  };

  if (!isOpen || !item) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-lg bg-dark-800 rounded-xl shadow-2xl border border-dark-700 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
                         <div>
               <h2 className="text-xl font-bold text-white">
                 {isAlreadyWatched ? 'Redaguoti peržiūrėjimą' : 'Pažymėti kaip peržiūrėtą'}
               </h2>
               <p className="text-gray-400 text-sm">
                 {item.title || item.name} ({new Date(item.release_date || item.first_air_date).getFullYear()})
               </p>
             </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-dark-700 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rating Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Įvertinimas (neprivalomas)
            </label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={`star-${star}`}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="text-2xl transition-colors duration-150 hover:scale-110 transform"
                >
                  <svg
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-600'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                </button>
              ))}
              {rating > 0 && (
                <button
                  type="button"
                  onClick={() => setRating(0)}
                  className="ml-2 text-gray-400 hover:text-gray-300 text-sm"
                >
                  Išvalyti
                </button>
              )}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-400 mt-2">
                {rating === 1 && 'Labai blogai'}
                {rating === 2 && 'Blogai'}
                {rating === 3 && 'Gerai'}
                {rating === 4 && 'Labai gerai'}
                {rating === 5 && 'Puikiai'}
              </p>
            )}
          </div>

          {/* Template Words */}
          {rating > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Greiti žodžiai
              </label>
              <div className="flex flex-wrap gap-2">
                {currentTemplateWords.map((word, index) => (
                  <button
                    key={`template-word-${word}-${index}`}
                    type="button"
                    onClick={() => handleTemplateWordClick(word)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      rating < 3
                        ? 'bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-600/30'
                        : 'bg-green-600/20 text-green-300 hover:bg-green-600/30 border border-green-600/30'
                    }`}
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Komentaras (neprivalomas)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={INPUT_LIMITS.COMMENT_MAX_LENGTH}
              rows={4}
              className="input-primary w-full resize-none"
              placeholder="Parašykite savo mintis apie šį filmą/serialą..."
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">
                Maksimalus ilgis: {INPUT_LIMITS.COMMENT_MAX_LENGTH} simbolių
              </p>
              <p className="text-xs text-gray-500">
                {comment.length}/{INPUT_LIMITS.COMMENT_MAX_LENGTH}
              </p>
            </div>
          </div>

                     {/* Actions */}
           <div className="flex items-center justify-between pt-4 border-t border-dark-700">
             <div>
               {isAlreadyWatched && (
                 <button
                   type="button"
                   onClick={handleRemove}
                   disabled={isLoading}
                   className={cn(BUTTON_CLASSES.DANGER_GHOST, 'px-4 py-2 font-medium flex items-center space-x-2', BUTTON_CLASSES.DISABLED)}
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                   </svg>
                   <span>Pašalinti</span>
                 </button>
               )}
             </div>
             <div className="flex items-center space-x-3">
               <button
                 type="button"
                 onClick={handleClose}
                 className="px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium"
               >
                 Atšaukti
               </button>
               <button
                 type="submit"
                 disabled={isLoading}
                 className={cn('btn-primary px-6 py-2 flex items-center space-x-2', BUTTON_CLASSES.DISABLED)}
               >
                 {isLoading ? (
                   <>
                     <div className={SPINNER_CLASSES.MEDIUM_WHITE}></div>
                     <span>Išsaugoma...</span>
                   </>
                 ) : (
                   <>
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                     </svg>
                     <span>{isAlreadyWatched ? 'Atnaujinti' : 'Išsaugoti'}</span>
                   </>
                 )}
               </button>
             </div>
           </div>
        </form>
      </div>
    </div>
  );
}

export default WatchedModal; 